import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Card,
  Upload,
  Image,
  Row,
  Col,
  Divider,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";

const WebsiteContent = () => {
  const [websiteContents, setWebsiteContents] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchWebsiteContents();
  }, []);

  const fetchWebsiteContents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}website-content`,
        {
          headers: {
            "x-product-type": "PIP_GRID",
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        },
      );
      setWebsiteContents(response.data.data || []);
    } catch (error) {
      console.error("Error fetching website contents:", error);
      message.error("Failed to fetch website contents");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingContent(null);
    setFileList([]);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (content) => {
    setEditingContent(content);
    
    // Convert single key-value to multiple key-value format for editing
    const keyValuePairs = content.keyValues || [{
      key: content.key,
      value: content.value
    }];
    
    form.setFieldsValue({
      section: content.section,
      keyValuePairs: keyValuePairs,
      imageUrl: content.imageUrl,
    });
    
    if (content?.imagePresignedUrl) {
      setFileList([
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: content?.imagePresignedUrl,
        },
      ]);
    } else {
      setFileList([]);
    }
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Delete Content",
      content: "Are you sure you want to delete this content?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          await axios.delete(
            `${process.env.REACT_APP_ADMIN_API_URL}website-content/${id}`,
            {
              headers: {
                "x-product-type": "PIP_GRID",
                Authorization: `Bearer ${localStorage.getItem("userToken")}`,
              },
            },
          );
          message.success("Content deleted successfully");
          fetchWebsiteContents();
        } catch (error) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to delete content";
          message.error(errorMessage);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleImageUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-product-type": "PIP_GRID",
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        },
      );
      onSuccess(response.data);
      return response.data;
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Image upload failed");
      onError(error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const formData = new FormData();
      formData.append("section", values.section);
      
      // Add each key-value pair as separate form fields for proper backend parsing
      values.keyValuePairs?.forEach((kv, index) => {
        formData.append(`keyValues[${index}][key]`, kv.key);
        formData.append(`keyValues[${index}][value]`, kv.value);
      });

      // If there's a new image being uploaded
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("image", fileList[0].originFileObj);
      }

      let response;
      if (editingContent) {
        response = await axios.put(
          `${process.env.REACT_APP_ADMIN_API_URL}website-content/${editingContent._id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "x-product-type": "PIP_GRID",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          },
        );
        message.success("Content updated successfully!");
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_ADMIN_API_URL}website-content`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "x-product-type": "PIP_GRID",
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          },
        );
        message.success("Content created successfully!");
      }

      setIsModalVisible(false);
      setFileList([]);
      fetchWebsiteContents();
    } catch (error) {
      console.error("Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong";
      message.error(
        `Failed to ${editingContent ? "update" : "create"} content: ${errorMessage}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
      form.setFieldsValue({ imageUrl: undefined });
      return true;
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error("Image must be smaller than 5MB!");
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    fileList,
    listType: "picture-card",
    maxCount: 1,
    customRequest: handleImageUpload,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
  };

  const columns = [
    {
      title: "Section",
      dataIndex: "section",
      key: "section",
      sorter: (a, b) => a.section.localeCompare(b.section),
    },
    {
      title: "Key-Value Pairs",
      dataIndex: "keyValues",
      key: "keyValues",
      render: (keyValues, record) => {
        // Handle both old format (single key-value) and new format (multiple key-value pairs)
        const pairs = keyValues || [{ key: record.key, value: record.value }];
        
        return (
          <div style={{ maxWidth: 300 }}>
            {pairs.map((pair, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <strong>{pair.key}:</strong>
                <div style={{ 
                  whiteSpace: "pre-wrap", 
                  wordBreak: "break-word",
                  fontSize: "12px",
                  color: "#666"
                }}>
                  {pair.value?.length > 100 ? `${pair.value.substring(0, 100)}...` : pair.value}
                </div>
                {index < pairs.length - 1 && <Divider style={{ margin: "4px 0" }} />}
              </div>
            ))}
          </div>
        );
      },
    },
    // Update the image column in the columns array
    {
      title: "Image",
      dataIndex: "imagePresignedUrl", // Changed from imageUrl to imagePresignedUrl
      key: "imagePresignedUrl",
      render: (imagePresignedUrl) =>
        imagePresignedUrl ? (
          <Image
            src={imagePresignedUrl}
            alt="Content"
            style={{ width: 50, height: 50, objectFit: "cover" }}
            preview={{
              src: imagePresignedUrl,
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="website-content-container">
      <Card
        title="Website Content Management"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
            Add Content
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={websiteContents}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        title={editingContent ? "Edit Content" : "Add New Content"}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          form.resetFields();
          setFileList([]);
          setIsModalVisible(false);
        }}
        confirmLoading={loading || uploading}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            section: "",
            keyValuePairs: [{ key: "", value: "" }],
          }}
        >
          <Form.Item
            name="section"
            label="Section"
            rules={[{ required: true, message: "Please input the section!" }]}
          >
            <Input placeholder="e.g., header, footer, about" />
          </Form.Item>

          <Form.Item label="Key-Value Pairs">
            <Form.List name="keyValuePairs">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ marginBottom: 16 }}>
                      <Row gutter={16} align="middle">
                        <Col span={10}>
                          <Form.Item
                            {...restField}
                            name={[name, 'key']}
                            rules={[{ required: true, message: 'Please input the key!' }]}
                          >
                            <Input placeholder="e.g., title, description, address" />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            {...restField}
                            name={[name, 'value']}
                            rules={[{ required: true, message: 'Please input the value!' }]}
                          >
                            <Input.TextArea rows={2} placeholder="Enter the content value" />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          {fields.length > 1 && (
                            <Button
                              type="text"
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => remove(name)}
                            />
                          )}
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Key-Value Pair
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            label="Image"
            name="imageUrl"
            extra="Upload an image (optional, max 5MB)"
          >
            <Upload
              {...uploadProps}
              listType="picture-card"
              className="image-uploader"
              accept="image/*"
              showUploadList={{
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WebsiteContent;
